import { useCallback, useEffect, useState } from 'react';
import { marketingApi, type MarketingSegment } from '@/api/marketing.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SegmentRuleBuilder, defaultSegmentRules } from './components/SegmentRuleBuilder';
import type { SegmentRulesPayload } from '@/api/marketing.api';
import { showApiError } from '@/utils/apiError';
import { MarketingEmptyState } from './components/MarketingEmptyState';
import { notify } from '@/core/toast/notify';

export function MarketingSegmentsPage() {
  const [segments, setSegments] = useState<MarketingSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [rules, setRules] = useState<SegmentRulesPayload>(defaultSegmentRules);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSegments(await marketingApi.listSegments());
    } catch (err) {
      showApiError(err, 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await marketingApi.createSegment({
        segmentName: name.trim() || 'Audience segment',
        segmentType: 'DYNAMIC',
        rulesJson: JSON.stringify(rules),
      });
      notify.success('Segment saved');
      setName('');
      setPreviewCount(null);
      load();
    } catch (err) {
      showApiError(err, 'Failed to create segment');
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    try {
      const members = await marketingApi.previewSegment('DYNAMIC', JSON.stringify(rules));
      setPreviewCount(members.length);
    } catch (err) {
      showApiError(err, 'Preview failed');
    }
  };

  const refresh = async (id: number) => {
    try {
      await marketingApi.refreshSegment(id);
      notify.success('Audience count updated');
      load();
    } catch (err) {
      showApiError(err, 'Refresh failed');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audiences"
        description="Build dynamic segments from CRM leads, POS customers, and lead-gen visitors — no duplicate contact storage."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Marketing', to: '/marketing' },
          { label: 'Segments' },
        ]}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="card p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-800">Rule builder</h2>
          <Input
            placeholder="Segment name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <SegmentRuleBuilder rules={rules} onChange={setRules} />
          <div className="flex flex-wrap gap-2">
            <Button loading={saving} onClick={() => void create()}>
              Save segment
            </Button>
            <Button variant="secondary" onClick={() => void preview()}>
              Preview audience
            </Button>
          </div>
          {previewCount !== null && (
            <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">
              Estimated matches: {previewCount.toLocaleString()}
            </p>
          )}
        </section>

        <section className="card">
          <h2 className="p-4 text-sm font-semibold border-b border-slate-100 text-slate-800 dark:border-slate-800 dark:text-slate-100">Saved audiences</h2>
          {loading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : segments.length === 0 ? (
            <MarketingEmptyState title="No segments" description="Save your first audience to use in campaigns." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {segments.map((s) => (
                <li key={s.id} className="p-4 flex flex-wrap justify-between items-center gap-2 text-sm">
                  <div>
                    <p className="font-medium text-slate-800">{s.segmentName}</p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {s.segmentType} · {s.estimatedCount.toLocaleString()} contacts
                    </p>
                  </div>
                  <Button variant="ghost" className="text-xs" onClick={() => void refresh(s.id)}>
                    Refresh
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

